import * as React from 'react';

// =============================================================================
// use-anchor-reflow.ts — Ré-ancrage d'un panneau PORTALÉ sur son déclencheur (U1-bis).
// =============================================================================
// RETOUR GATE_HUMAN du 2026-09-18 (verbatim) : « la list of values des filtres apparaît en mode
// floating quand on zoom sur la page ». Un panneau portalé dans <body> en `position: fixed` est ancré
// aux coordonnées du déclencheur au moment de l'ouverture : tout ce qui déplace le déclencheur SANS
// émettre l'événement écouté le laisse « flotter » loin de son bouton.
//
// Les trois sources de déplacement, toutes couvertes ici :
//   1. DÉFILEMENT, y compris celui d'un conteneur interne ⇒ `scroll` en CAPTURE sur window ;
//   2. REDIMENSIONNEMENT de la fenêtre ⇒ `resize` sur window ;
//   3. ZOOM du navigateur (Ctrl + molette, pincement tactile) ⇒ `visualViewport` : le zoom visuel
//      déplace le viewport VISUEL sans forcément émettre un `resize` de window, et un élément en
//      `position: fixed` reste, lui, calé sur le viewport de MISE EN PAGE. Sans ces deux écouteurs, le
//      menu se décolle exactement comme décrit. `visualViewport` est absent d'anciens navigateurs et de
//      jsdom : l'accès est défensif (jamais de throw, le reste des écouteurs suffit).
//
// Le panneau est RÉ-ANCRÉ, jamais fermé (précédent `menu-button.tsx`, bug ST-CREATION-DUP 2026-09-16 :
// fermer au défilement rendait le menu inouvrable quand le focus du déclencheur faisait défiler son
// conteneur).

/**
 * Branche le ré-ancrage d'un panneau ouvert. `reflow` est appelé à chaque défilement, redimensionnement
 * ou changement de zoom tant que `active` est vrai. Ne fait rien quand le panneau est fermé.
 */
export function useAnchorReflow(active: boolean, reflow: () => void): void {
  React.useEffect(() => {
    if (!active) {
      return;
    }
    const onReflow = (): void => {
      reflow();
    };
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    // Viewport VISUEL (zoom navigateur / pincement) : absent d'anciens navigateurs et de jsdom.
    const viewport: VisualViewport | undefined = window.visualViewport ?? undefined;
    viewport?.addEventListener('resize', onReflow);
    viewport?.addEventListener('scroll', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
      viewport?.removeEventListener('resize', onReflow);
      viewport?.removeEventListener('scroll', onReflow);
    };
  }, [active, reflow]);
}
